import * as React from 'react';
import PropTypes from 'prop-types';

import UIDateFormater, {
  DateType,
} from '../../../containers/UIhelper/UIDateFormater';

import { Table, Column, Cell } from 'fixed-data-table-2';
import { useDispatch } from 'react-redux';
import {
  sortG5Devices,
  storeG5DevicesAction,
} from '../../../actions/DeviceConfigurationAction';
import SortableHeaderCell from '../../SortableHeaderCell/SortableHeaderCell';

import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button } from '@mui/material';
import useProjectTimezone from '../../../hooks/useProjectTimeZone';

const CustomeCell = (props) => (
  <Cell>{props.items[props.rowIndex][props.columnKey]}</Cell>
);
const CellVX = (props) => {
  const value = props.items[props.rowIndex][props.columnKey];
  if (value === 0) return <Cell>&#10007;</Cell>; // ✗
  if (value === 1) return <Cell>&#10003;</Cell>; // ✓
  return value;
}

const ServerFieldCell = (props) => {
  const val = props.items[props.rowIndex][props.columnKey];
  const valDisplay = val ? val.split('.')[0] : '--';
  return <Cell>{valDisplay}</Cell>;
};

const AgcThresholdCell = (props) => {
  const val = props.items[props.rowIndex][props.columnKey];
  return <Cell>{val == null ? '--' : val}</Cell>;
};

const AGC_GAIN_LABEL_KEYS = {
  0: 'auto_gain',
  1: 'gain_1',
  2: 'gain_2',
  3: 'gain_3',
  4: 'gain_4',
  5: 'auto_filter',
};

const AgcGainCell = (props, context) => {
  const val = props.items[props.rowIndex][props.columnKey];
  const key = AGC_GAIN_LABEL_KEYS[val];
  return <Cell>{key ? context.t(key) : '--'}</Cell>;
};
AgcGainCell.contextTypes = {
  t: PropTypes.func.isRequired,
};

const PSM_STATUS_LABEL_KEYS = {
  0: 'psm_state_none',
  1: 'psm_state_network',
  2: 'psm_state_push',
};

const PsmStatusCell = (props, context) => {
  const val = props.items[props.rowIndex][props.columnKey];
  const key = PSM_STATUS_LABEL_KEYS[val];
  return <Cell>{key ? context.t(key) : '--'}</Cell>;
};
PsmStatusCell.contextTypes = {
  t: PropTypes.func.isRequired,
};

const PsmIntervalCell = (props) => {
  const val = props.items[props.rowIndex][props.columnKey];
  return <Cell>{val == null ? '--' : val}</Cell>;
};

function StatusesTable(props, context) {
  const { items, sort, local, uiInfo, options } = props;
  const projectTimezone = useProjectTimezone();

  const [selectedItemIndex, setSelectedItemIndex] = React.useState(-1);
  const parsedItems = items.map((item) => {
    const option = options.FM2ConfigType.find(
      (option) => option.code === item.fmReceiverChannel2_Config
    );
    return {
      ...item,
      fmReceiverChannel2_Config_name: option?.name,
    };
  });

  const dispatch = useDispatch();

  const [preFilterItems, setPreFilterItems] = React.useState([]);
  const lastSelectedIndex = React.useRef();

  function renderBatteryField(value) {
    return value ? value.toFixed(3) : '--';
  }

  const getMultiRowsBySelection = (index) => {
    const start = Math.min(index, lastSelectedIndex.current);
    const end = Math.max(index, lastSelectedIndex.current);
    const multiSelect = items
      .filter((_item, itemIndex) => itemIndex >= start && itemIndex <= end)
      .map((item) => item.DeviceID);
    return multiSelect;
  };

  const filterCheckboxRender = (item, index) => (
    <Cell>
      <input
        type='checkbox'
        checked={preFilterItems.indexOf(item.DeviceID) !== -1}
        onChange={(event) => {
          const { checked } = event.target;
          const { shiftKey } = event.nativeEvent;
          let preFiltersArr = [...preFilterItems];
          if (checked) {
            if (!shiftKey) {
              preFiltersArr.push(item.DeviceID);
            } else {
              const multiSelect = getMultiRowsBySelection(index);
              preFiltersArr.push(...multiSelect);
            }
          } else {
            if (!shiftKey) {
              // unchecked selected
              preFiltersArr = preFiltersArr.filter(
                (itemDeviceId) => itemDeviceId !== item.DeviceID
              );
            } else {
              const multiSelect = getMultiRowsBySelection(index);
              preFiltersArr = preFiltersArr.filter(
                (itemDeviceId) => !multiSelect.includes(itemDeviceId)
              );
            }
          }
          setPreFilterItems(preFiltersArr);
          lastSelectedIndex.current = index;
        }}
      />
    </Cell>
  );

  const handleRowClick = (event, rowIndex) => {
    setSelectedItemIndex(rowIndex);
  };

  const rowClassNameGetter = (rowIndex) => {
    return rowIndex === selectedItemIndex ? 'active-row' : null;
  };

  const sortTable = {
    sortDir: sort.direction,
    sortBy: sort.field,
    sort: (columnKey) => {
      dispatch(sortG5Devices(columnKey));
      setSelectedItemIndex(-1);
    },
  };

  return (
    <section>
      <Table
        rowsCount={parsedItems.length}
        width={uiInfo.pageWidth / 1.1635}
        maxHeight={uiInfo.pageHeight / 1.0909}
        headerHeight={50}
        rowHeight={40}
        onRowClick={handleRowClick}
        rowClassNameGetter={rowClassNameGetter}
      >
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey=''
          width={60}
          header={
            <Cell>
              <Button
                variant='contained'
                onClick={() => {
                  props.handleSelection(preFilterItems);
                  setPreFilterItems([]);
                }}
              >
                {context.t('filter')}
              </Button>
            </Cell>
          }
          cell={(props) =>
            filterCheckboxRender(parsedItems[props.rowIndex], props.rowIndex)
          }
        />
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey='#'
          width={40}
          header={<Cell>#</Cell>}
          cell={(props) => <Cell>{props.rowIndex + 1}</Cell>}
        />
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey='ID'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey='DeviceID'
          width={140}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey='DeviceType'
          width={80}
          header={<Cell>{context.t('device_type') || 'Type'}</Cell>}
          cell={(props) => {
            const device = parsedItems[props.rowIndex];
            const deviceType = device.isMini === 1 ? 'Ultra' : 'Edge';
            return <Cell>{deviceType}</Cell>;
          }}
        />
        <Column
          fixed
          cellClassName='fixed-table-cell-class'
          align='center'
          columnKey='LastStatusReportTime'
          width={140}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={(props) => {
            const value = parsedItems[props.rowIndex][props.columnKey];
            const time = UIDateFormater(value, DateType.DATE_AND_TIME, local , projectTimezone);
            return <Cell>{time}</Cell>;
          }}
        />
        <Column
          align='center'
          columnKey='BatteryLevel'
          width={75}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={(props) => {
            const value = parsedItems[props.rowIndex][props.columnKey];
            return <Cell>{renderBatteryField(value)}</Cell>;
          }}
        />
        <Column
          align='center'
          columnKey='BatteryLevelPost'
          width={75}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={(props) => {
            const value = parsedItems[props.rowIndex][props.columnKey];
            return <Cell>{renderBatteryField(value)}</Cell>;
          }}
        />
        <Column
          align='center'
          columnKey='AccessTechnologyName'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={(props) => (
            <Cell>{parsedItems[props.rowIndex][props.columnKey]}</Cell>
          )}
        />
        <Column
          align='center'
          columnKey='IEMI'
          width={140}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          align='center'
          columnKey='ICCID'
          width={180}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          align='center'
          columnKey='StatusTimeZone'
          width={160}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='APN'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='HwType'
          width={70}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={(props) => {
            const value = parsedItems[props.rowIndex]['DeviceType'];
            const hwTypeMap = { 1: 'BG_INT', 2: 'BG_EXT', 3: 'AG' };
            return <Cell>{hwTypeMap[value] || '--'}</Cell>;
          }}
        />
        <Column
          columnKey='HardwareRevision'
          width={70}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='FirmwareRevision'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='RSRQ_db'
          width={60}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='RSRP_dbm'
          width={60}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='FmReceiverChannel_10k'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='FmReceiverChannel2_10k'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='fm_channel_snr'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='fm_channel2_snr'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='agc_threshold1'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<AgcThresholdCell items={parsedItems} />}
        />
        <Column
          columnKey='agc_threshold2'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<AgcThresholdCell items={parsedItems} />}
        />
        <Column
          columnKey='agc_threshold3'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<AgcThresholdCell items={parsedItems} />}
        />
        <Column
          columnKey='agc_sensor_gain'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<AgcGainCell items={parsedItems} />}
        />
        <Column
          columnKey='fmReceiverChannel2_Config_name'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='Server'
          width={100}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<ServerFieldCell items={parsedItems} />}
        />
        <Column
          columnKey='FallbackServer'
          width={100}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<ServerFieldCell items={parsedItems} />}
        />
        <Column
          columnKey='time_source'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={(props) => {
            const value = parsedItems[props.rowIndex][props.columnKey];
            if (value === 0) {
              return <Cell>{'TLV'}</Cell>;
            } else {
              return <Cell>{'Cellular'}</Cell>;
            }
          }}
        />
        <Column
          align='center'
          columnKey='is_valve_monitor'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CellVX items={parsedItems} />}
        />
        <Column
          columnKey='ad_hoc_corr'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CellVX items={parsedItems} />}
        />
        <Column
          align='center'
          columnKey='is_burst_monitor'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CellVX items={parsedItems} />}
        />
        <Column
          columnKey='isMini'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CellVX items={parsedItems} />}
        />
        <Column
          columnKey='pdp_type'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='pco_type'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='psm_status'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<PsmStatusCell items={parsedItems} />}
        />
        <Column
          columnKey='psm_interval_sec'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<PsmIntervalCell items={parsedItems} />}
        />
        <Column
          columnKey='modem_fw_rev'
          width={120}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='dfu_fw_rev'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='dfu_type'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='dfu_block_num'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
        <Column
          columnKey='random_backoff_connect'
          width={80}
          header={<SortableHeaderCell sortTable={sortTable} />}
          cell={<CustomeCell items={parsedItems} />}
        />
      </Table>
      <footer style={{ backgroundColor: 'white' }}>
        <IconButton onClick={props.reloadDevices}>
          <RefreshIcon fontSize='small' />
        </IconButton>

        <p style={{ float: 'right', margin: '5px 10px' }}>
          <span>
            {context.t('selected_param', {
              selectedItems: selectedItemIndex + 1,
            })}
          </span>
          &emsp;
          <span>
            {parsedItems.length} {context.t('items')}
          </span>
        </p>
      </footer>
    </section>
  );
}

StatusesTable.contextTypes = {
  t: PropTypes.func.isRequired,
};

StatusesTable.propTypes = {
  items: PropTypes.array.isRequired,
  local: PropTypes.string,
  reloadDevices: PropTypes.func.isRequired,
  selectedItems: PropTypes.array.isRequired,
  workingMode: PropTypes.string.isRequired,
};

export default StatusesTable;
